import { mergeWith } from 'lodash';
import { isArray } from 'util';

import {
    getTargetingFilterCriteria,
    RetargetingCriteriaKey
} from '../declarations/filter/filter';
import {
    FilterExpression,
    FilterPartialExpression,
    RetargetingsQueryFilter,
    StatsQueryFilter
} from '../typings/filter';
import { GdRetargetingBaseStatusInput } from '../typings/graphql-grid';

import {
    FilterCriteriaMappings,
    getStrictExpressions,
    textToRetargetingFilterExpression
} from './filter/filter';

const mappings = new FilterCriteriaMappings<
    RetargetingsQueryFilter & StatsQueryFilter
>();
const { toNumber, toEnum } = mappings.valueMappings;

const retargetingsCriteriaMappings: {
    [key in RetargetingCriteriaKey]: (
        expression: FilterExpression
    ) => RetargetingsQueryFilter
} = {
    name: mappings.contains('nameContains', 'nameNotContains'),
    statusIn: mappings.equality(
        'statusIn',
        toEnum(GdRetargetingBaseStatusInput)
    ),
    price: mappings.comparison(
        'minPriceContext',
        'maxPriceContext',
        toNumber,
        false
    ),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison(
        'minProfitability',
        'maxProfitability',
        toNumber
    ),
    avgGoalCost: mappings.comparison(
        'minAvgGoalCost',
        'maxAvgGoalCost',
        toNumber
    ),
    avgClickCost: mappings.comparison(
        'minAvgClickCost',
        'maxAvgClickCost',
        toNumber,
        true
    ),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: mappings.comparison('minGoals', 'maxGoals', toNumber),
    conversionRate: mappings.comparison(
        'minConversionRate',
        'maxConversionRate',
        toNumber
    ),
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber)
};

const expressionToRetargetingQueryFilter = (
    expression: FilterExpression
): RetargetingsQueryFilter => {
    return retargetingsCriteriaMappings[expression.criteriaKey](expression);
};

export const mapRetargetingFilterToApiFormat = (
    expressions?: FilterPartialExpression[]
): RetargetingsQueryFilter => {
    expressions = expressions || [];
    const criteria = getTargetingFilterCriteria();

    return getStrictExpressions(
        expressions,
        criteria,
        textToRetargetingFilterExpression
    )
        .map(expressionToRetargetingQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};
